import {HttpRequest} from "@angular/common/http";
import {AbstractControl, AsyncValidatorFn, ValidationErrors} from "@angular/forms";
import {map, Observable, of} from "rxjs";
import {ProjectType, UbicacionItem} from "../../users/interfaces/proyecto.interface";
import {computed, Signal} from '@angular/core';

export function isNumeric(input: any): boolean {
    if (input === null || input === undefined) {
        return false;
    }
    const str = input.toString();
    if (str.trim() === '') {
        return false;
    }
    return !isNaN(Number(str)) && !isNaN(parseFloat(str));
}

/**
 * Este metodo de validacion no permite ingresar el '-' para numeros negativos.
 *Caso 1: permite ingresar números enteros, números decimales, y utlizar tecla tab, y delete.
 *Caso 2: permite ingresar solo numero enteros y utlizar tecla tab, y delete.
 *Caso 3: permite ingresar solo caracteres, numeros enteros y utlizar tecla tab, delete, flecha de izquierda y derecha.
 *Caso 4: permite ingresar solo caracteres utlizar tecla tab, y delete.
 **/
export function isAlphaNumericValidEvent(event: KeyboardEvent, call: number): void {
    const char = event.key;
    switch (call) {
        case 1:
            if (!/[0-9.]/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'Tab') {
                return event.preventDefault();
            }
            break;
        case 2:
            if (!/^[0-9]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'Tab') {
                return event.preventDefault();
            }
            break;
        case 3:
            if (!/^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ0-9\s]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'Tab' && char !== 'ArrowLeft' && char !== 'ArrowRight') {
                event.preventDefault();
            }
            break;
        case 4:
            if (!/^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'Tab' && char !== 'ArrowLeft' && char !== 'ArrowRight') {
                event.preventDefault();
            }
            break;
        default:
            break;
    }
}

/**
 * Este metodo valida valores de porcentaje en un input
 * solo permite valores entre 0 a 100.00
 * se utliza en formulario c*/
export function isValidPercentageEvent(value: number, event: any): number {
    const inputElemento = event.target as HTMLInputElement;
    let inputValor = inputElemento.value;
    const numeroPatron = /^\d{1,3}(\.\d{0,2})?$/;
    const numeroValor = parseFloat(inputValor);
    // Valida si el valor es un número antes de proceder
    if (isNaN(parseFloat(inputValor))) {
        inputElemento.value = '';
        return 0;
    }
    if (!numeroPatron.test(inputValor)) {
        inputValor = numeroValor.toFixed(2);
        inputElemento.value = inputValor;
    }
    if (numeroValor < 0) {
        value = 0;
        inputElemento.value = value.toFixed(2);
    } else if (numeroValor > 100) {
        value = 100;
        inputElemento.value = value.toFixed(2);
    } else {
        value = numeroValor;
    }
    return value;
}

/**
 * Este metodo valida cantidades de un input
 * solo permite valores entre 0 a 100
 * se utliza en formulario c*/
export function isValidQuantityEvent(value: number, event: any): number {
    const inputElemento = event.target as HTMLInputElement;
    let inputValor = inputElemento.value;
    inputValor = inputValor.replace(/[^0-9]/g, '');
    const numeroValor = parseInt(inputValor);
    // Valida si el valor es un número antes de proceder
    if (isNaN(numeroValor)) {
        inputElemento.value = '';
        return 0;
    }
    if (numeroValor < 0) {
        value = 0;
    } else if (numeroValor > 100) {
        value = 100;
    } else {
        value = numeroValor;
    }
    inputElemento.value = value.toString();
    return value;
}

export function convertDate(fecha: Date) {
    if (fecha == null) {
        return fecha;
    }
    const dateString = `${fecha.getDate()}/${fecha.getMonth() + 1}/${fecha.getFullYear()}`;
    return encodeURIComponent(dateString);
}

export function isValidLatitude(event: any): boolean {
    const inputElemento = event.target as HTMLInputElement;
    if (!isNumeric(inputElemento)) {
        return false;
    }
    let latitude = parseFloat(inputElemento.value);
    return !isNaN(latitude) && latitude >= -90 && latitude <= 90;
}

export function isValidLongitude(event: any): boolean {
    const inputElemento = event.target as HTMLInputElement;
    if (!isNumeric(inputElemento)) {
        return false;
    }
    let longitude = Number.parseFloat(inputElemento.value);
    return !isNaN(longitude) && longitude >= -180 && longitude <= 180;
}

export function getUrl(req: HttpRequest<any>) {
    if (req.url.startsWith("assets/")) {
        return null;
    }
    return new URL(req.url).pathname;
}

export function randomNumber(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min) + min);
}

export function spanishNameAsyncValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
        const value = control.value;
        const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-]+$/; // Allow letters, accents, ñ, spaces, and hyphens
        return of(value).pipe(
            map((val) => {
                if (val && !nameRegex.test(val)) {
                    return {invalidName: true}; // Return validation error
                }
                return null;
            })
        );
    };
}

export function removeSpecialCharacters(input: string): string {
    return input.replace(/[\s-]/g, '');
}

export function isValidObject(input: any): boolean {
    return input != null
}

export function isValidText(input: string | undefined | null): boolean {
    return input != null && input != "" && input != "0"
}

export function isValidNumber(input: number): boolean {
    return input != null && input != 0 && input > 0
}

export function isValidUbicacion(item: UbicacionItem): boolean {
    const tieneMatriculaYClave = isValidText(item.matricula) && isValidText(item.claveCatastral); // Flujo 1 completo
    const tieneCoordenadasValidas = isNumeric(item.latitud) && isNumeric(item.longitud); // Flujo 2 completo
    console.log(`#DEBUG C0:ubicacionItem: ${JSON.stringify(item)}`)
    if (tieneMatriculaYClave) {
        console.log(`#DEBUG C1:tieneMatriculaYClave: ${tieneMatriculaYClave}`);
        if (item.claveCatastral.length > 500 || item.matricula.length > 500) return false;
        return true;
    } else if (tieneCoordenadasValidas) {
        const esLatitudValida = isNumeric((item.latitud))
        const esLongitudValida = isNumeric((item.longitud))
        console.log(`#DEBUG C2:latitud: ${item.latitud} longitud: ${item.longitud} esLatitudValida: ${esLatitudValida} esLongitudValida: ${esLongitudValida}`);
        return !(!esLatitudValida || !esLongitudValida);
    } else {
        console.log(`#DEBUG C3:tieneMatriculaYClave: ${tieneMatriculaYClave}`);
        console.log(`#DEBUG C3:tieneCoordenadasValidas: ${tieneCoordenadasValidas}`);
        return false;
    }
}

export function parseToFormatNumber(value: number | null): string {
    if (value === null || value === undefined) {
        return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(value);
}

/**Este metodo parsea los valores booleanos a si o no de las preguntas seleccionas en el FU*/
export function parseBooleanToText(value: boolean | null): string {
    if (value === true) return 'Si';
    if (value === false) return 'No';
    return 'No definido';
}

/**
 * Valida si el tipo actual de proyecto coincide con uno o varios tipos dados.
 * @param currentType Señal reactiva con el tipo actual del proyecto
 * @param types Uno o varios tipos de proyecto
 * @returns Signal<boolean> que indica si hay coincidencia
 */
export function isProjectType(currentType: Signal<ProjectType | null>, ...types: ProjectType[]) {
    return computed(() => {
        const value = currentType();
        return value !== null && types.includes(value);
    });
}

export function isValidStr(input: string | undefined | null): boolean {
    return input != null && input.trim().length > 0;
}

export const SOLICITUD_RE =
    /^(?<year>\d{4})(?<month>0[1-9]|1[0-2])[-\u2013\u2014\u2212](?<seq>[1-9]\d{0,3})$/;

export function isSolicitud(v: unknown): v is string {
    if (typeof v !== 'string') return false;
    return SOLICITUD_RE.test(v.trim());
}

const DASHES = /[-\u2013\u2014\u2212]/g;

export function canonSolicitud(s: string) {
    return s.trim().replace(DASHES, '-').replace(/\s+/g, '');
}

export function isSolicitudPrefix(v: unknown): v is string {
    if (typeof v !== 'string') return false;
    const c = canonSolicitud(v);
    return /^\d{4}(\d{0,2})(-\d{0,4})?$/.test(c);
}

export function escapeLike(s: string) {
    return s.replace(/[\\%_]/g, m => '\\' + m);
}

export function escapeLineJumps(value: string | null | undefined): string | undefined {
    return value?.replace(/\r?\n/g, "\\n");
}
